import { useFormContext, useWatch } from 'react-hook-form';
import { Grid, Card, CardHeader, CardContent, Typography } from '@mui/material';

import { officeApiKeyStore } from '../../../api/office-api-key-store';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { FormSelect } from '../../../components/hook-form/form-select.component';
import { divisionApiKeyStore } from '../../../api/masters/division-api-key-store';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { rebateCompanyApiKeyStore } from '../../../api/masters/rebate-company-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useEffect } from 'react';
import { SelectFieldOption } from '../../../components/hook-form/hook-form-error';
import { FormAutoCompleteSelectDynamic } from '../../../components/hook-form/form-autocomplete-select.dynamic.component';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { customersApiKeyStore } from '../../../api/customers-api-key-store';
import { useSaveData } from '../../../hooks/useSaveData';

const rebateOptions: SelectFieldOption[] = [
  { value: 'processing', label: 'Processing', isDefault: false },
  { value: 'notProcessing', label: 'Not Processing', isDefault: false },
  // { value: "notqualify", label: 'Does Not Qualify', isDefault: false },
  // { value: "norebate", label: 'No Rebate Program Available', isDefault: false },
];

const styling = {
  headingStyle: { fontWeight: 'bold' },
};

interface OfficeInfoForm {
  isCustomerLoading: boolean;
  customerID: number;
  rebateValue: string;
}

const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

export const OfficeInfoForm = ({ isCustomerLoading, customerID, rebateValue }: OfficeInfoForm) => {
  const { getValues, setValue } = useFormContext();

  const { data, isLoading } = useLoadData<ApiResponse<any>>(
    officeApiKeyStore.getOfficeCode(customerID),
    customerID !== 0
  );

  useEffect(() => {
    if (data) {
      setValue('officeCode', customerID ? data?.data : '');
    }
  }, [data, customerID]);

  useWatch({
    name: ['rebates', 'syncWithDuxCustomer', 'syncWithDux'] || [],
  });

  const orderOption = [
    {
      value: true,
      label: 'Yes',
      isDefault: false,
    },
    {
      value: false,
      label: 'No',
      isDefault: true,
    },
  ];

  const handleRebate = (value: any) => {
    if (value !== 'processing') {
      setValue('rebateCompanies', null);
    }
  };

  const isSyncWithDuxPermission = useAuthorize([UserPermissionName?.SYNC_WITH_DUX]);
  const {
    isLoading: isloadingSyuncWithDux,
    mutateAsync,
    data: duxSyncStatusChangeData,
  } = useSaveData<any>(customersApiKeyStore.duxSyncStatusChange());

  const handleChangeSyncWithDux = (e: any) => {
    setValue('syncWithDux', !e.target.checked);
    mutateAsync({
      data: {
        projectID: 0,
        officeID: !getValues('officeID') ? 0 : getValues('officeID'),
        customerID: 0,
        syncWithDux: e.target.checked,
      },
    }).then(() => {
      setValue('syncWithDux', !e.target.checked);
    });
  };

  return (
    <Card elevation={0}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            OFFICE INFO
          </Typography>
        }
      ></CardHeader>
      <CardContent>
        <Grid container spacing={2}>
          <Grid item lg={5.5} sm={5.5} xl={5.5} xs={5.5}>
            <Grid container spacing={2}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormInput
                  name="officeName"
                  label="Office Name"
                  validationQueryRoute={(value: string) =>
                    getValues('customer.customerID') &&
                    officeApiKeyStore.validateOffice(
                      getValues('customer.customerID') ?? 0,
                      getValues('officeID') ?? 0,
                      value
                    )
                  }
                />
              </Grid>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormInput name="description" label="Description" multiline={true} size={3.5} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <LoadingIndicator isLoading={isCustomerLoading} />
                <FormSelect
                  name="requirePurchaseOrder"
                  label="Require Purchase Order"
                  options={orderOption}
                  // required
                />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <LoadingIndicator isLoading={isCustomerLoading} />
                <FormSelect name="rebates" label="Rebates" onChange={handleRebate} options={rebateOptions} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={6.5} sm={6.5} xl={6.5} xs={6.5}>
            <Grid container spacing={2}>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <LoadingIndicator isLoading={isLoading || isCustomerLoading} />
                <FormInput name="officeCode" label="Office Code" disabled />
              </Grid>
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormInput name="builderCode" label="Builder Code" disabled={getValues('officeID')} />
              </Grid>

              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <FormInput type="number" name="reportDeliveryTimeDays" label="Report Delivery Time (In Days)" />
              </Grid>

              <Grid item lg={5.5} sm={5.5} xl={5.5} xs={5.5}>
                <FormAutoCompleteMultiSelectDynamic
                  name="officeDivisions"
                  label="Divisions"
                  watchKeys={['customer.customerID']}
                  getQueryKeyConfig={() => divisionApiKeyStore.getCustomerDivisions(getValues('customer.customerID'))}
                  optionsLabelKey="divisionName"
                  optionsValueKey="divisionID"
                  disableCloseOnSelect={true}
                />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <FormCheckbox label="Active" name="isActive" />
              </Grid>
              {isDUXEnabled && isSyncWithDuxPermission && (
                <Grid item lg={4} sm={4} xl={4} xs={4}>
                  {<LoadingIndicator isLoading={isloadingSyuncWithDux} />}
                  <FormCheckbox
                    label="Sync With DUX"
                    name="syncWithDux"
                    disabled={!getValues('syncWithDuxCustomer')}
                    onChange={!getValues('officeID') ? undefined : handleChangeSyncWithDux}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
